/**
 * UI store — global UI state (currency, locale, theme, etc.)
 * Currency is managed by admin only — fetched from backend config.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'

interface UIState {
  currency: string
  locale: string
  currencyLoaded: boolean
  setCurrency: (currency: string) => void
  setLocale: (locale: string) => void
  fetchCurrency: () => Promise<void>
}

const DEFAULT_CURRENCY = import.meta.env.VITE_BASE_CURRENCY ?? 'INR'
const DEFAULT_LOCALE = import.meta.env.VITE_DEFAULT_LOCALE ?? 'en-US'

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      currency: DEFAULT_CURRENCY,
      locale: DEFAULT_LOCALE,
      currencyLoaded: false,
      setCurrency: (currency) => set({ currency }),
      setLocale: (locale) => set({ locale }),
      fetchCurrency: async () => {
        try {
          const { data } = await api.get<{ currency: string; symbol: string }>('/config/currency')
          set({ currency: data.currency, currencyLoaded: true })
        } catch {
          // Fallback to default if API fails
          set({ currencyLoaded: true })
        }
      },
    }),
    {
      name: 'ui-preferences',
      partialize: (state) => ({ locale: state.locale }),
    },
  ),
)

// Currency is loaded during app bootstrap (AppBootstrapGate)
